import { useCallback } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Button, Modal, Textarea } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { ONBOARDING_AGENT_NEGATIVE_FEEDBACK_MODAL_CUSTOM_STYLES } from "../onboardingAgentModalStyles";
import {
    ONBOARDING_AGENT_FEEDBACK_MODAL_CANCEL,
    ONBOARDING_AGENT_FEEDBACK_MODAL_PLACEHOLDER,
    ONBOARDING_AGENT_FEEDBACK_MODAL_SUBMIT,
    ONBOARDING_AGENT_FEEDBACK_MODAL_TITLE,
} from "../texts";

const NEGATIVE_FEEDBACK_TEXTAREA_HEIGHT = "147px";

type OnboardingAgentNegativeFeedbackModalProps = {
    isOpen: boolean;
    messageIndex: number;
    comment: string;
    onCommentChange: (value: string) => void;
    onClose: () => void;
    onSubmit: () => void;
};

export const OnboardingAgentNegativeFeedbackModal = ({
    isOpen,
    messageIndex,
    comment,
    onCommentChange,
    onClose,
    onSubmit,
}: OnboardingAgentNegativeFeedbackModalProps) => {
    const { translate } = useTranslate();
    const baseAutomation = `onboarding-agent-message-${messageIndex}-feedback-modal`;

    const handleSubmit = useCallback((): void => {
        if (!comment.trim()) {
            return;
        }

        onSubmit();
    }, [comment, onSubmit]);

    const canSubmit = comment.trim().length > 0;

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            customStyles={ONBOARDING_AGENT_NEGATIVE_FEEDBACK_MODAL_CUSTOM_STYLES}
            data-automation={baseAutomation}
        >
            <Modal.Header>
                <Modal.Title>{translate(ONBOARDING_AGENT_FEEDBACK_MODAL_TITLE)}</Modal.Title>
            </Modal.Header>

            <Modal.Content>
                <div data-automation={`${baseAutomation}-textarea`}>
                    <Textarea
                        value={comment}
                        onChange={onCommentChange}
                        placeholder={translate(ONBOARDING_AGENT_FEEDBACK_MODAL_PLACEHOLDER)}
                        height={NEGATIVE_FEEDBACK_TEXTAREA_HEIGHT}
                    />
                </div>
            </Modal.Content>

            <Modal.Footer>
                <Flex
                    width="100%"
                    justifyContent="flex-end"
                    gap="4px"
                >
                    <Button
                        type="flat"
                        dataAutomation={`${baseAutomation}-cancel`}
                        onClick={onClose}
                    >
                        {translate(ONBOARDING_AGENT_FEEDBACK_MODAL_CANCEL)}
                    </Button>
                    <Button
                        type="primary"
                        dataAutomation={`${baseAutomation}-submit`}
                        isDisabled={!canSubmit}
                        onClick={handleSubmit}
                    >
                        {translate(ONBOARDING_AGENT_FEEDBACK_MODAL_SUBMIT)}
                    </Button>
                </Flex>
            </Modal.Footer>
        </Modal>
    );
};
